import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Music, User, MessageSquare, Heart, Check, Trash2, 
  QrCode, Settings, LogOut, Maximize, Minimize,
  Clock, Filter, RefreshCw, Volume2, Youtube, Ban,
  Play, Pause, Eye, ExternalLink, X, AlertCircle, Power, Bell, BellOff,
  Link as LinkIcon, CalendarDays, Square, Flame, Crown, Globe, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { LANGUAGE_FLAGS, LANGUAGE_NAMES } from '@/i18n/translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge text for pro status display
const getProBadgeText = (user) => {
  // Debug log
  console.log('[BADGE DEBUG] User data:', {
    is_pro_lifetime: user?.is_pro_lifetime,
    pro_status: user?.pro_status,
    plan: user?.plan,
    pro_effective: user?.pro_effective
  });
  
  // Check for lifetime status (handle both boolean and string)
  const isLifetime = user?.is_pro_lifetime === true || user?.is_pro_lifetime === 'true' || user?.pro_status === 'lifetime';
  if (isLifetime) return '🟡 Lifetime Gold';
  if (user?.pro_status === 'trialing') return '👑 PRO (Essai)';
  if (user?.pro_status === 'active') return '🟢 Fondateur – 4,99€ à vie';
  
  // Also check pro_effective as fallback
  if (user?.pro_effective === true) return '👑 PRO';
  
  return 'FREE';
};

// Notification sound fallback - Simple base64 encoded beep
const NOTIFICATION_SOUND_FALLBACK = 'data:audio/wav;base64,UklGRl9vT19teleQcZS6/g5bGBJBBFrN3psYY0Fkao3vO3hjspLIPW+ryJRUFRlNT/xY9hVGeS0v/Lm3F3dIvN/8yZeYKKh87/x5F3g4qG0v/Lj3OKjIXQ/8yYeIOGicn/xot0ho6Gzv/MnXuEh4fI/8SEbomOicn/xoJxjYyNzP/GjXuJiYrL/8aCcoyJicr/x4R0jIeKyP/Hg3SLiYnL/8qJeYuGhsn/yYh2i4WJyf/Ih3aKhonJ/8eHdouFi8v/yIZ1ioWKyP/GhXaMiYrJ/8eGdoqGicj/x4d3i4WIyP/HhXWLhoXK/8eFdYuHhsr/yIZ0i4eGy//IhXWMhofK/8mHeI2Ghsr/yYZ3jYaHyP/JhneMhoXL/8mGdo6Hhsr/yYd3jYaHyf/JhniNhoXL/8mHd46GhcjAA';

// Helper function to detect media type
const getMediaType = (url) => {
  if (!url) return null;
  const lower = url.toLowerCase();
  if (lower.includes('spotify.com')) return 'spotify';
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'youtube';
  return 'link';
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, isPro, refreshUser } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [statusFilter, setStatusFilter] = useState('active'); // Default: hide played requests
  const [spotifyOnly, setSpotifyOnly] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const lastRequestCountRef = useRef(0);
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  
  // Refuse modal state
  const [refuseModalOpen, setRefuseModalOpen] = useState(false);
  const [requestToRefuse, setRequestToRefuse] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [refuseLoading, setRefuseLoading] = useState(false);
  
  // Requests open/closed state
  const [requestsOpen, setRequestsOpen] = useState(true);
  const [togglingRequests, setTogglingRequests] = useState(false);
  
  // Push notifications state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showAudioUnlockHint, setShowAudioUnlockHint] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const previousNewRequestsRef = useRef([]);

  // Refuse reasons with translations
  const REFUSE_REASONS_TRANSLATED = {
    already_played: t('dashboard', 'refuse_already_played') || "Déjà passée pendant la soirée",
    not_in_theme: t('dashboard', 'refuse_not_in_theme') || "Ne correspond pas au thème de la soirée",
    already_requested: t('dashboard', 'refuse_already_requested') || "Déjà demandée par quelqu'un d'autre"
  };

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      setNotificationsEnabled(Notification.permission === 'granted' && localStorage.getItem('dj_notifications') === 'true');
    }
  }, []);

  // Force refresh user data on mount to ensure we have latest pro status
  useEffect(() => {
    if (refreshUser) {
      console.log('[DASHBOARD] Forcing user refresh on mount...');
      refreshUser().then(() => {
        console.log('[DASHBOARD] User refreshed');
      }).catch(e => {
        console.warn('[DASHBOARD] User refresh failed:', e);
      });
    }
  }, [refreshUser]);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      alert('Les notifications ne sont pas supportées par ce navigateur');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        localStorage.setItem('dj_notifications', 'true');
        // Send test notification
        new Notification('🎵 TrackVibes', {
          body: 'Les notifications sont activées ! Tu seras alerté des nouvelles demandes.',
          icon: '/assets/trackvibes-logo.png',
          tag: 'test-notification'
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  // Toggle notifications
  const toggleNotifications = () => {
    if (notificationPermission !== 'granted') {
      requestNotificationPermission();
    } else {
      const newValue = !notificationsEnabled;
      setNotificationsEnabled(newValue);
      localStorage.setItem('dj_notifications', newValue.toString());
    }
  };

  // Send push notification for new request
  const sendPushNotification = useCallback((request) => {
    if (!notificationsEnabled || notificationPermission !== 'granted') return;
    
    try {
      const notification = new Notification('🎵 Nouvelle demande !', {
        body: `${request.client_name} demande "${request.song_title}"${request.artist ? ` de ${request.artist}` : ''}`,
        icon: '/assets/trackvibes-logo.png',
        tag: `request-${request.id}`,
        requireInteraction: true,
        vibrate: [200, 100, 200]
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  }, [notificationsEnabled, notificationPermission]);

  const fetchRequests = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        status_filter: statusFilter,
        spotify_only: spotifyOnly
      });
      
      const [requestsRes, statsRes, requestsStatusRes] = await Promise.all([
        axios.get(`${API}/requests?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/dj/requests-status`, { headers: getAuthHeader() })
      ]);
      
      // Check for new requests and play sound + push notification
      const currentNewRequests = requestsRes.data.filter(r => r.is_new);
      const newCount = currentNewRequests.length;
      
      if (lastRequestCountRef.current > 0 && newCount > lastRequestCountRef.current) {
        // Find truly new requests (not in previous list)
        const previousIds = previousNewRequestsRef.current.map(r => r.id);
        const brandNewRequests = currentNewRequests.filter(r => !previousIds.includes(r.id));
        
        if (brandNewRequests.length > 0) {
          // Play sound
          if (soundEnabled) {
            playNotificationSound();
          }
          
          // Send push notification for each new request
          brandNewRequests.forEach(request => {
            sendPushNotification(request);
          });
        }
      }
      
      lastRequestCountRef.current = newCount;
      previousNewRequestsRef.current = currentNewRequests;
      
      setRequests(requestsRes.data);
      setStats(statsRes.data);
      setRequestsOpen(requestsStatusRes.data.requests_open);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, sortBy, statusFilter, spotifyOnly, soundEnabled, sendPushNotification]);

  // Intelligent polling: 5 seconds, pauses when tab is inactive
  useEffect(() => {
    fetchRequests();
    let interval = null;
    
    const startPolling = () => {
      if (!interval) {
        interval = setInterval(fetchRequests, 5000); // Poll every 5 seconds
      }
    };
    
    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchRequests(); // Immediate refresh when tab becomes active
        startPolling();
      }
    };
    
    // Start polling
    startPolling();
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchRequests]);

  const playNotificationSound = async () => {
    // Don't attempt to play if sound is disabled
    if (!soundEnabled) return;
    
    try {
      // Try Web Audio API first for better quality sound
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext && audioUnlockedRef.current) {
        const audioContext = new AudioContext();
        const duration = 0.12;
        const sampleRate = audioContext.sampleRate;
        const buffer = audioContext.createBuffer(1, sampleRate * duration, sampleRate);
        const data = buffer.getChannelData(0);
        
        // Generate a pleasant "pop" notification sound
        for (let i = 0; i < buffer.length; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 25); // Quick decay
          const freq1 = 880; // A5 note
          const freq2 = 1320; // E6 note (harmony)
          data[i] = envelope * (
            0.5 * Math.sin(2 * Math.PI * freq1 * t) +
            0.3 * Math.sin(2 * Math.PI * freq2 * t) +
            0.2 * Math.sin(2 * Math.PI * freq1 * 1.5 * t)
          );
        }
        
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0.6; // Volume at 60%
        
        source.connect(gainNode);
        gainNode.connect(audioContext.destination);
        source.start();
        
        // Clean up after sound plays
        source.onended = () => audioContext.close();
        return;
      }
      
      // Fallback to HTML5 Audio
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        audioUnlockedRef.current = true;
        setShowAudioUnlockHint(false);
      }
    } catch (e) {
      // NotAllowedError or NotSupportedError - browser blocked autoplay
      if (e.name === 'NotAllowedError' || e.name === 'NotSupportedError') {
        console.warn('Notification sound blocked by browser - user interaction required');
        if (soundEnabled && !audioUnlockedRef.current) {
          setShowAudioUnlockHint(true);
        }
      } else {
        console.warn('Could not play notification sound:', e.message);
      }
    }
  };

  // Unlock audio on first user interaction
  const unlockAudio = async () => {
    if (audioUnlockedRef.current) return;
    
    try {
      if (audioRef.current) {
        // Play a silent/short sound to unlock audio context
        audioRef.current.volume = 0.01;
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        audioRef.current.pause();
        audioRef.current.volume = 1;
        audioRef.current.currentTime = 0;
        audioUnlockedRef.current = true;
        setShowAudioUnlockHint(false);
        console.log('Audio unlocked successfully');
      }
    } catch (e) {
      // Still blocked, will try again on next interaction
      console.warn('Audio unlock failed, will retry on next interaction');
    }
  };

  // Add event listeners to unlock audio on first interaction
  useEffect(() => {
    const handleUserInteraction = () => {
      unlockAudio();
    };

    // Listen for user interactions to unlock audio
    document.addEventListener('click', handleUserInteraction, { once: false, passive: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: false, passive: true });
    document.addEventListener('keydown', handleUserInteraction, { once: false, passive: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/requests/${requestId}/status`, 
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      fetchRequests();
    } catch (error) {
      console.error('Error updating status');
    }
  };

  const handleMarkSeen = async (requestId) => {
    try {
      await axios.patch(`${API}/requests/${requestId}/mark-seen`, {}, { headers: getAuthHeader() });
      fetchRequests();
    } catch (error) {
      console.error('Error marking seen');
    }
  };

  const handleDelete = async (requestId) => {
    try {
      await axios.delete(`${API}/requests/${requestId}`, { headers: getAuthHeader() });
      fetchRequests();
    } catch (error) {
      console.error('Error deleting');
    }
  };

  const handleBlockClient = async (clientId) => {
    if (!clientId) return;
    try {
      await axios.post(`${API}/dj/block-client/${clientId}`, {}, { headers: getAuthHeader() });
      fetchRequests();
    } catch (error) {
      console.error('Error blocking client');
    }
  };

  // Open refuse modal
  const openRefuseModal = (request) => {
    setRequestToRefuse(request);
    setSelectedReason(null);
    setRefuseModalOpen(true);
  };

  // Close refuse modal
  const closeRefuseModal = () => {
    setRefuseModalOpen(false);
    setRequestToRefuse(null);
    setSelectedReason(null);
  };

  // Handle refuse confirmation
  const handleRefuseConfirm = async () => {
    if (!requestToRefuse || !selectedReason) return;
    
    setRefuseLoading(true);
    try {
      await axios.patch(
        `${API}/requests/${requestToRefuse.id}/refuse`,
        { reason: selectedReason },
        { headers: getAuthHeader() }
      );
      fetchRequests();
      closeRefuseModal();
    } catch (error) {
      console.error('Error refusing request');
    } finally {
      setRefuseLoading(false);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Toggle requests open/closed
  const handleToggleRequests = async () => {
    setTogglingRequests(true);
    try {
      const newStatus = !requestsOpen;
      await axios.patch(
        `${API}/dj/requests-status`,
        { requests_open: newStatus },
        { headers: getAuthHeader() }
      );
      setRequestsOpen(newStatus);
    } catch (error) {
      console.error('Error toggling requests status:', error);
    } finally {
      setTogglingRequests(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'received':
        return <Badge className="bg-blue-500 text-white">{t('dashboard', 'status_received')}</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500 text-black">{t('dashboard', 'status_pending')}</Badge>;
      case 'played':
        return <Badge className="bg-green-500 text-white">{t('dashboard', 'status_played')}</Badge>;
      case 'refused':
        return <Badge className="bg-red-500 text-white">{t('dashboard', 'status_refused')}</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neon-cyan animate-pulse font-heading text-2xl">{t('common', 'loading')}</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#050505] ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      <audio ref={audioRef} src={NOTIFICATION_SOUND_FALLBACK} preload="auto" />
      
      {/* Navigation */}
      <nav className="bg-[#0a0a0a] border-b border-white/10 px-3 sm:px-4 md:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink">
            <h1 className="font-heading text-lg sm:text-xl md:text-2xl whitespace-nowrap">
              <span className="text-neon-cyan neon-text">DJ</span>{' '}
              <span className="text-white hidden sm:inline">{user?.dj_name}</span>
            </h1>
            {/* Plan Badge - Show PRO or FREE status using centralized isPro() */}
            <Badge 
              className={`${isPro() ? 'bg-neon-pink text-white' : 'bg-gray-600 text-gray-200'} text-[10px] sm:text-xs px-2 py-0.5 whitespace-nowrap flex-shrink-0`}
              data-testid="plan-badge"
            >
              {getProBadgeText(user)}
            </Badge>
            
            {/* Upgrade to PRO button - only for FREE users */}
            {!isPro() && (
              <Link to="/settings?tab=subscription" className="flex-shrink-0">
                <Button 
                  size="sm" 
                  className="bg-gradient-to-r from-neon-pink to-purple-500 hover:shadow-[0_0_20px_rgba(255,0,153,0.4)] text-white text-[10px] sm:text-xs font-semibold rounded-full px-2 sm:px-4 py-1"
                  data-testid="upgrade-pro-header-btn"
                >
                  <Crown className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">{t('dashboard', 'upgrade_pro')}</span>
                </Button>
              </Link>
            )}
            
            {stats.new_requests > 0 && (
              <Badge className="bg-red-500 text-white animate-pulse text-[10px] sm:text-xs px-2 py-0.5 whitespace-nowrap hidden sm:flex">
                {stats.new_requests} {stats.new_requests > 1 ? t('dashboard', 'new_count_plural') : t('dashboard', 'new_count')}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSoundEnabled(!soundEnabled);
                // Try to unlock audio when user clicks sound button
                if (!soundEnabled) unlockAudio();
              }}
              className={`text-gray-400 hover:text-white ${soundEnabled ? 'text-neon-cyan' : ''}`}
              title={soundEnabled ? t('dashboard', 'sound_on') : t('dashboard', 'sound_off')}
            >
              <Volume2 className="w-5 h-5" />
            </Button>
            
            {/* Audio unlock hint */}
            {showAudioUnlockHint && soundEnabled && (
              <span 
                className="text-xs text-amber-400 animate-pulse cursor-pointer hidden sm:inline"
                onClick={unlockAudio}
              >
                {t('dashboard', 'tap_to_enable_sound')}
              </span>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleNotifications}
              className={`text-gray-400 hover:text-white ${notificationsEnabled ? 'text-neon-pink' : ''}`}
              title={notificationsEnabled ? t('dashboard', 'notifications_on') : t('dashboard', 'notifications_off')}
              data-testid="toggle-notifications-btn"
            >
              {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
            </Button>
            
            {/* Events link - only shown for PRO users */}
            {isPro() && (
              <Link to="/events">
                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-neon-pink" data-testid="events-nav-btn">
                  <CalendarDays className="w-5 h-5" />
                </Button>
              </Link>
            )}
            
            <Link to="/qrcode">
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-neon-cyan">
                <QrCode className="w-5 h-5" />
              </Button>
            </Link>
            
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-neon-cyan">
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="text-gray-400 hover:text-neon-cyan hidden md:flex"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-gray-400 hover:text-red-500"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Notification Banner - Show if notifications not enabled */}
        {notificationPermission !== 'granted' && (
          <div className="bg-neon-pink/10 border border-neon-pink/30 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-neon-pink" />
              <div>
                <p className="text-neon-pink font-semibold text-sm">{t('dashboard', 'enable_notifications')}</p>
                <p className="text-gray-400 text-xs">{t('dashboard', 'enable_notifications_desc')}</p>
              </div>
            </div>
            <Button
              onClick={requestNotificationPermission}
              className="bg-neon-pink text-black hover:bg-neon-pink/80 text-sm"
              size="sm"
            >
              {t('dashboard', 'enable_btn')}
            </Button>
          </div>
        )}

        {/* Offre Fondateur Banner - Show for FREE users */}
        {!isPro() && (
          <div className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
            <p className="text-yellow-400 text-sm font-medium text-center">
              🎉 {t('dashboard', 'founder_offer')}
            </p>
            <div className="text-center mt-2">
              <Link to="/settings?tab=subscription">
                <Button 
                  size="sm" 
                  className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold hover:shadow-[0_0_20px_rgba(255,200,0,0.4)]"
                >
                  <Crown className="w-3 h-3 mr-1" />
                  {t('dashboard', 'founder_cta')}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className={`grid grid-cols-2 ${isPro() ? 'md:grid-cols-6' : 'md:grid-cols-5'} gap-3 md:gap-4 mb-6`}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
            <p className="text-gray-400 text-xs md:text-sm mb-1">{t('dashboard', 'stat_received')}</p>
            <p className="font-heading text-2xl md:text-3xl text-blue-500">{stats.received_requests || 0}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
            <p className="text-gray-400 text-xs md:text-sm mb-1">{t('dashboard', 'stat_pending')}</p>
            <p className="font-heading text-2xl md:text-3xl text-yellow-500">{stats.pending_requests || 0}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
            <p className="text-gray-400 text-xs md:text-sm mb-1">{t('dashboard', 'stat_played')}</p>
            <p className="font-heading text-2xl md:text-3xl text-green-500">{stats.played_requests || 0}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
            <p className="text-gray-400 text-xs md:text-sm mb-1">{t('dashboard', 'stat_refused')}</p>
            <p className="font-heading text-2xl md:text-3xl text-red-500">{stats.refused_requests || 0}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
            <p className="text-gray-400 text-xs md:text-sm mb-1">{t('dashboard', 'stat_total')}</p>
            <p className="font-heading text-2xl md:text-3xl text-white">{stats.total_requests || 0}</p>
          </div>
          {/* Only show Tips stat for PRO users */}
          {isPro() && (
            <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
              <p className="text-gray-400 text-xs md:text-sm mb-1">{t('dashboard', 'stat_tips')}</p>
              <p className="font-heading text-2xl md:text-3xl text-neon-pink">{stats.donations_count || 0}</p>
            </div>
          )}
        </div>

        {/* Requests Open/Close Toggle */}
        <div className={`rounded-xl p-4 mb-6 border ${requestsOpen ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Power className={`w-5 h-5 ${requestsOpen ? 'text-green-500' : 'text-red-500'}`} />
              <div>
                <p className={`font-semibold ${requestsOpen ? 'text-green-400' : 'text-red-400'}`}>
                  {requestsOpen ? t('dashboard', 'requests_open') : t('dashboard', 'requests_closed')}
                </p>
                <p className="text-gray-400 text-xs">
                  {requestsOpen 
                    ? t('dashboard', 'requests_open_desc')
                    : t('dashboard', 'requests_closed_desc')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm hidden sm:inline">
                {requestsOpen ? 'ON' : 'OFF'}
              </span>
              <Switch
                checked={requestsOpen}
                onCheckedChange={handleToggleRequests}
                disabled={togglingRequests}
                data-testid="toggle-requests-switch"
                className={`${requestsOpen ? 'bg-green-500' : 'bg-red-500'} ${togglingRequests ? 'opacity-50' : ''}`}
              />
            </div>
          </div>
        </div>

        {/* Active Event Banner - PRO only */}
        {isPro() && stats.active_event && (
          <Link to="/events" className="block">
            <div className="bg-gradient-to-r from-neon-cyan/10 to-neon-pink/10 border border-neon-cyan/30 rounded-xl p-4 mb-6 flex items-center justify-between hover:border-neon-cyan/50 transition-all" data-testid="active-event-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-neon-cyan/20 rounded-full flex items-center justify-center">
                  <Play className="w-5 h-5 text-neon-cyan" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs">{t('dashboard', 'event_live')}</p>
                  <p className="text-white font-semibold">{stats.active_event.name}</p>
                </div>
              </div>
              <Badge className="bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30">
                <span className="w-2 h-2 bg-neon-cyan rounded-full mr-2 animate-pulse"></span>
                {t('dashboard', 'event_live_badge')}
              </Badge>
            </div>
          </Link>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h2 className="font-heading text-xl text-white">{t('dashboard', 'requests')}</h2>
          
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchRequests}
              className="text-gray-400 hover:text-neon-cyan"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{t('dashboard', 'refresh')}</span>
            </Button>

            {/* Status Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="border-white/20 text-gray-300">
                  <Filter className="w-4 h-4 mr-1" />
                  {statusFilter === 'active' ? t('dashboard', 'filter_active') :
                   statusFilter === 'all' ? t('dashboard', 'filter_all') : 
                   statusFilter === 'received' ? t('dashboard', 'filter_received') : 
                   statusFilter === 'pending' ? t('dashboard', 'filter_pending') : 
                   statusFilter === 'played' ? t('dashboard', 'filter_played') : t('dashboard', 'filter_refused')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#111] border-white/10">
                <DropdownMenuItem onClick={() => setStatusFilter('active')} className="text-gray-300 focus:bg-white/10">
                  {t('dashboard', 'filter_active')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('all')} className="text-gray-300 focus:bg-white/10">
                  {t('dashboard', 'filter_all')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => setStatusFilter('received')} className="text-gray-300 focus:bg-white/10">
                  {t('dashboard', 'filter_received')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('pending')} className="text-gray-300 focus:bg-white/10">
                  {t('dashboard', 'filter_pending')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('played')} className="text-green-400 focus:bg-white/10">
                  {t('dashboard', 'filter_played')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('refused')} className="text-red-400 focus:bg-white/10">
                  {t('dashboard', 'filter_refused')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant={spotifyOnly ? "secondary" : "outline"}
              size="sm"
              onClick={() => setSpotifyOnly(!spotifyOnly)}
              className={spotifyOnly ? "bg-green-500/20 text-green-500 border-green-500/50" : "border-white/20 text-gray-300"}
            >
              🎵 Spotify
            </Button>
          </div>
        </div>

        {/* Requests List */}
        <div className="space-y-3">
          {requests.length === 0 ? (
            <div className="text-center py-16">
              <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">{t('dashboard', 'no_requests')}</p>
            </div>
          ) : (
            requests.map((request) => (
              <div
                key={request.id}
                className={`bg-[#111] border rounded-xl p-4 ${
                  request.is_new ? 'border-neon-cyan animate-pulse' : 'border-white/5'
                } ${request.status === 'played' ? 'opacity-60' : ''} ${request.status === 'refused' ? 'opacity-50 border-red-500/20' : ''}`}
                onClick={() => request.is_new && handleMarkSeen(request.id)}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-neon-cyan font-medium flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {request.client_name}
                      </span>
                      <span className="text-gray-500 text-sm flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(request.created_at)}
                      </span>
                      {getStatusBadge(request.status)}
                      {request.is_new && (
                        <Badge className="bg-red-500 text-white text-xs">
                          <Eye className="w-3 h-3 mr-1" />
                          {t('dashboard', 'badge_new')}
                        </Badge>
                      )}
                      {request.has_donation && (
                        <Badge className="bg-gradient-to-r from-pink-500 to-purple-500 text-white">
                          <Heart className="w-3 h-3 mr-1 fill-current" />
                          {request.donation_amount ? `${request.donation_amount}€` : t('dashboard', 'badge_tip')}
                        </Badge>
                      )}
                      {/* Phase 3: Enhanced likes display with "Très soutenu" badge */}
                      {(request.likes >= 3) && (
                        <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white animate-pulse">
                          <Flame className="w-3 h-3 mr-1 fill-current" />
                          🔥 {t('dashboard', 'badge_very_supported')}
                        </Badge>
                      )}
                      {(request.likes > 0 && request.likes < 3) && (
                        <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
                          <Flame className="w-3 h-3 mr-1" />
                          {request.likes} {request.likes > 1 ? t('dashboard', 'badge_votes') : t('dashboard', 'badge_vote')}
                        </Badge>
                      )}
                    </div>
                    
                    <h3 className="font-heading text-xl text-white mb-1">
                      {request.song_title}
                    </h3>
                    
                    {request.artist && (
                      <p className="text-gray-400 mb-2">{request.artist}</p>
                    )}

                    {/* BPM Badge - DJ only, discreet display */}
                    {request.bpm && (
                      <div className="mb-2">
                        <Badge 
                          className="bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs font-mono"
                          data-testid="bpm-badge"
                        >
                          <Activity className="w-3 h-3 mr-1" />
                          {request.bpm} BPM
                        </Badge>
                      </div>
                    )}

                    {/* Links */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {/* New media_url field */}
                      {request.media_url && (
                        <a
                          href={request.media_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg hover:opacity-80 text-sm ${
                            getMediaType(request.media_url) === 'spotify' 
                              ? 'bg-green-500/20 text-green-500' 
                              : getMediaType(request.media_url) === 'youtube'
                              ? 'bg-red-500/20 text-red-500'
                              : 'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          {getMediaType(request.media_url) === 'spotify' ? (
                            <>🎵 Spotify</>
                          ) : getMediaType(request.media_url) === 'youtube' ? (
                            <><Youtube className="w-4 h-4" /> YouTube</>
                          ) : (
                            <><LinkIcon className="w-4 h-4" /> {t('dashboard', 'link_label')}</>
                          )}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {/* Legacy spotify_link field */}
                      {request.spotify_link && (
                        <a
                          href={request.spotify_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 text-sm"
                        >
                          🎵 Spotify
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {/* Legacy youtube_link field */}
                      {request.youtube_link && (
                        <a
                          href={request.youtube_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 text-sm"
                        >
                          <Youtube className="w-4 h-4" />
                          YouTube
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    
                    {request.message && (
                      <p className="text-gray-500 text-sm flex items-start gap-2 mt-3 p-3 bg-white/5 rounded-lg">
                        <MessageSquare className="w-4 h-4 mt-0.5 text-neon-pink flex-shrink-0" />
                        <span className="italic">"{request.message}"</span>
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex md:flex-col items-center gap-2 flex-shrink-0">
                    {request.status !== 'pending' && request.status !== 'refused' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(request.id, 'pending')}
                        className="bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 border border-yellow-500/50"
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">{t('dashboard', 'action_pending')}</span>
                      </Button>
                    )}
                    {request.status !== 'played' && request.status !== 'refused' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(request.id, 'played')}
                        className="bg-green-500/20 text-green-500 hover:bg-green-500/30 border border-green-500/50"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">{t('dashboard', 'action_played')}</span>
                      </Button>
                    )}
                    {request.status !== 'refused' && (
                      <Button
                        size="sm"
                        onClick={() => openRefuseModal(request)}
                        className="bg-red-500/20 text-red-500 hover:bg-red-500/30 border border-red-500/50"
                      >
                        <X className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">{t('dashboard', 'action_refuse')}</span>
                      </Button>
                    )}
                    {request.status === 'refused' && request.refused_reason && (
                      <div className="text-xs text-red-400 text-center max-w-[150px]">
                        {REFUSE_REASONS_TRANSLATED[request.refused_reason]}
                      </div>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-gray-400">
                          •••
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#111] border-white/10">
                        {request.status === 'played' && (
                          <DropdownMenuItem 
                            onClick={() => handleStatusChange(request.id, 'received')}
                            className="text-gray-300 focus:bg-white/10"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            {t('dashboard', 'action_requeue')}
                          </DropdownMenuItem>
                        )}
                        {request.status === 'refused' && (
                          <DropdownMenuItem 
                            onClick={() => handleStatusChange(request.id, 'received')}
                            className="text-gray-300 focus:bg-white/10"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            {t('dashboard', 'action_cancel_refuse')}
                          </DropdownMenuItem>
                        )}
                        {/* Block client feature temporarily disabled for product simplification */}
                        {/* {request.client_id && (
                          <DropdownMenuItem 
                            onClick={() => handleBlockClient(request.client_id)}
                            className="text-orange-500 focus:bg-orange-500/10"
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Bloquer ce client
                          </DropdownMenuItem>
                        )} */}
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(request.id)}
                          className="text-red-500 focus:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t('dashboard', 'action_delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Refuse Modal */}
      {refuseModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl text-white flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-red-500" />
                {t('modals', 'refuse_title')}
              </h3>
              <button onClick={closeRefuseModal} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {requestToRefuse && (
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <p className="text-white font-semibold">{requestToRefuse.song_title}</p>
                {requestToRefuse.artist && <p className="text-gray-400 text-sm">{requestToRefuse.artist}</p>}
                <p className="text-neon-cyan text-sm mt-1">{t('dashboard', 'from_client')} : {requestToRefuse.client_name}</p>
              </div>
            )}

            <p className="text-gray-400 text-sm mb-4">{t('modals', 'refuse_subtitle')}</p>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => setSelectedReason('already_played')}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selectedReason === 'already_played'
                    ? 'bg-red-500/20 border-red-500 text-white'
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                }`}
              >
                🔄 {t('modals', 'refuse_reason_played')}
              </button>
              <button
                onClick={() => setSelectedReason('not_in_theme')}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selectedReason === 'not_in_theme'
                    ? 'bg-red-500/20 border-red-500 text-white'
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                }`}
              >
                🎭 {t('modals', 'refuse_reason_theme')}
              </button>
              <button
                onClick={() => setSelectedReason('already_requested')}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selectedReason === 'already_requested'
                    ? 'bg-red-500/20 border-red-500 text-white'
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                }`}
              >
                👥 {t('modals', 'refuse_reason_requested')}
              </button>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={closeRefuseModal}
                variant="outline"
                className="flex-1 border-white/20 text-gray-300"
              >
                {t('common', 'cancel')}
              </Button>
              <Button
                onClick={handleRefuseConfirm}
                disabled={!selectedReason || refuseLoading}
                className="flex-1 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                {refuseLoading ? t('common', 'loading') : t('modals', 'refuse_confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
